import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { ChildSelectorBar } from '../components/ChildSelectorBar';
import { Lightbulb, Clock, Target, Info, ArrowRight, Star, Zap, History, X } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { DjayNarrator } from '../../../mascot';
import { MODULES } from '../../../content';
import { getWeeklyActivityForLearner } from '../../../utils/activityHelpers';
import { type LearnerAccount, type ParentAccount } from '../../../accounts';
import { AppState } from '../../../app/types';
import { ParentV2Route } from '../ParentSidebarV2';
import { useChildDecay } from '../hooks/useChildDecay';
import { useModuleMastery } from '../hooks/useModuleMastery';
import { shuffleWithSeed } from '../../../utils/deterministicShuffle';
import { getParentTipDismissals, setParentTipDismissed, clearParentTipDismissal, isTipDismissed } from '../../../data/domains/preferences';
interface TipsPageProps {
  activeAccount: ParentAccount;
  linkedLearners: LearnerAccount[];
  selectedChild: LearnerAccount | undefined;
  childData: AppState | null;
  getLearnerModuleMastery: (learnerId: string, moduleId: string) => number;
  setViewingLearner: (id: string) => void;
  onNavigate?: (route: ParentV2Route) => void;
}
type TipColor = 'amber' | 'indigo' | 'emerald' | 'rose' | 'sky';
interface GeneratedTip {
  id: string;
  icon: React.ReactNode;
  title: string;
  content: string;
  color: TipColor;
  /** When true, the tip cannot be dismissed (e.g. permanent platform info). */
  pinned?: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
}
/**
 * ISO-week-of-year helper (deterministic shuffle seed for W4-2).
 * Same week → same order; next week → new order without backend.
 */
function getISOWeekOfYear(date: Date = new Date()): number {
  const target = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNr = (target.getUTCDay() + 6) % 7;
  target.setUTCDate(target.getUTCDate() - dayNr + 3);
  const firstThursday = new Date(Date.UTC(target.getUTCFullYear(), 0, 4));
  const diff = (target.getTime() - firstThursday.getTime()) / (24 * 60 * 60 * 1000);
  return 1 + Math.round((diff - 3 + (firstThursday.getUTCDay() + 6) % 7) / 7);
}
export function TipsPage({
  activeAccount,
  linkedLearners,
  selectedChild,
  childData,
  getLearnerModuleMastery,
  setViewingLearner,
  onNavigate
}: TipsPageProps) {
  const decay = useChildDecay(childData);
  const moduleMastery = useModuleMastery(selectedChild, getLearnerModuleMastery);
  // W4-2: parent-scoped dismissals. Loaded async; surface a stable empty
  // map first to avoid flashing dismissed cards in.
  const [dismissals, setDismissals] = useState<Record<string, number>>({});
  useEffect(() => {
    let cancelled = false;
    void getParentTipDismissals(activeAccount.id).then(next => {
      if (!cancelled) setDismissals(next);
    });
    return () => {
      cancelled = true;
    };
  }, [activeAccount.id]);
  const generatedTips = useMemo<GeneratedTip[]>(() => {
    if (!selectedChild || !childData) return [];
    const tips: GeneratedTip[] = [];
    // 1. Rhythm Tip
    const weeklyActivity = getWeeklyActivityForLearner(childData.questionStates || {});
    const daysPracticed = weeklyActivity.filter(d => d.practiced).length;
    if (daysPracticed === 0) {
      tips.push({
        id: 'rhythm-none',
        icon: <Clock className="w-6 h-6 text-amber-500" data-id="element-8283" />,
        title: 'Start met een vast ritme',
        content: `Probeer een vast moment op de dag te vinden voor ${selectedChild.displayName} om te oefenen. Bijvoorbeeld 10 minuten na het eten. Korte, regelmatige sessies werken veel beter dan één lange sessie in het weekend.`,
        color: 'amber'
      });
    } else if (daysPracticed < 3) {
      tips.push({
        id: 'rhythm-some',
        icon: <Clock className="w-6 h-6 text-indigo-500" data-id="element-8284" />,
        title: 'Bouw het ritme uit',
        content: `${selectedChild.displayName} heeft deze week ${daysPracticed} keer geoefend. Dat is een mooi begin! Probeer toe te werken naar 3 of 4 keer per week voor het beste leereffect.`,
        color: 'indigo'
      });
    } else {
      tips.push({
        id: 'rhythm-good',
        icon: <Clock className="w-6 h-6 text-emerald-500" data-id="element-8285" />,
        title: 'Houd dit ritme vast!',
        content: `Fantastisch! ${selectedChild.displayName} oefent heel regelmatig. Dit is de perfecte manier om nieuwe stof eigen te maken en te onthouden. Blijf dit stimuleren.`,
        color: 'emerald'
      });
    }
    // 2. Weakest module
    const strongest = moduleMastery[0] ?? null;
    const weakest = moduleMastery.length > 0 ? moduleMastery[moduleMastery.length - 1] : null;
    const weakestModule = weakest?.module ?? null;
    const lowestMastery = weakest?.mastery ?? 100;
    const strongestModule = strongest?.module ?? null;
    const highestMastery = strongest?.mastery ?? 0;
    if (weakestModule && lowestMastery < 50) {
      tips.push({
        id: `subject-weak-${weakestModule.id}`,
        icon: <Target className="w-6 h-6 text-rose-500" data-id="element-8286" />,
        title: `Extra aandacht voor ${weakestModule.title.toLowerCase()}`,
        content: `Het vak ${weakestModule.title.toLowerCase()} is nog wat lastig (${lowestMastery}% beheersing). Meester djay zal hier automatisch makkelijkere vragen over stellen om het vertrouwen op te bouwen. Je kunt helpen door in het dagelijks leven samen sommetjes of taalpuzzels te maken die hierbij passen.`,
        color: 'rose',
        action: {
          label: 'Bekijk voortgang',
          onClick: () => onNavigate?.('voortgang')
        }
      });
    }
    // 3. Strongest module praise
    if (strongestModule && highestMastery >= 70) {
      tips.push({
        id: `subject-strong-${strongestModule.id}`,
        icon: <Star className="w-6 h-6 text-emerald-500" data-id="element-8287" />,
        title: `Uitblinker in ${strongestModule.title.toLowerCase()}!`,
        content: `${selectedChild.displayName} gaat ontzettend goed met ${strongestModule.title.toLowerCase()} (${highestMastery}% beheersing). Geef een complimentje! Succeservaringen zijn heel belangrijk voor de motivatie.`,
        color: 'emerald'
      });
    }
    // 4. Streak
    const currentStreak = childData.stats?.streakState?.currentStreak || 0;
    if (currentStreak >= 3) {
      tips.push({
        id: 'streak-active',
        icon: <Zap className="w-6 h-6 text-amber-500" data-id="element-8288" />,
        title: `${currentStreak} dagen op rij geoefend!`,
        content: `Wat een inzet! ${selectedChild.displayName} heeft een 'streak' van ${currentStreak} dagen opgebouwd. Probeer deze reeks niet te verbreken, dat werkt enorm motiverend.`,
        color: 'amber'
      });
    }
    // 5. Mastery decay
    if (childData.familyMastery) {
      const decayingModules: Array<{
        module: (typeof MODULES)[number];
        worstStatus: 'fading' | 'stale';
        daysSince: number;
      }> = [];
      for (const module of MODULES) {
        const mastery = getLearnerModuleMastery(selectedChild.id, module.id);
        if (mastery <= 0) continue;
        const info = decay.perModule[module.id];
        if (!info || info.worstStatus === null) continue;
        decayingModules.push({
          module,
          worstStatus: info.worstStatus,
          daysSince: info.daysSince
        });
      }
      decayingModules.sort((a, b) => {
        if (a.worstStatus !== b.worstStatus) {
          return a.worstStatus === 'stale' ? -1 : 1;
        }
        return b.daysSince - a.daysSince;
      });
      decayingModules.slice(0, 2).forEach(dm => {
        const isStale = dm.worstStatus === 'stale';
        const weeksAgo = Math.floor(dm.daysSince / 7);
        const timeLabel = weeksAgo >= 4 ? `meer dan een maand` : weeksAgo >= 2 ? `${weeksAgo} weken` : `meer dan een week`;
        tips.push({
          id: `mastery-decay-${dm.module.id}`,
          icon: <History className={`w-6 h-6 ${isStale ? 'text-rose-500' : 'text-indigo-500'}`} data-id="element-8289" />,
          title: isStale ? `${dm.module.title} dreigt weg te zakken` : `Tijd voor herhaling: ${dm.module.title.toLowerCase()}`,
          content: isStale ? `${selectedChild.displayName} heeft al ${timeLabel} niet geoefend met ${dm.module.title.toLowerCase()}. De kennis begint weg te zakken. Even 10 minuten oefenen kan al veel helpen!` : `Het is ${timeLabel} geleden dat ${selectedChild.displayName} heeft geoefend met ${dm.module.title.toLowerCase()}. Regelmatig herhalen helpt om de stof goed te onthouden.`,
          color: isStale ? 'rose' : 'indigo',
          action: {
            label: 'Bekijk vakken',
            onClick: () => onNavigate?.('voortgang')
          }
        });
      });
    }
    // 6. Platform info — pinned, cannot be dismissed
    tips.push({
      id: 'platform-info',
      icon: <Info className="w-6 h-6 text-sky-500" data-id="element-8290" />,
      title: 'Hoe Meester djay werkt',
      content: 'Het systeem past zich automatisch aan het niveau van je kind aan. Als het goed gaat, worden de vragen uitdagender. Bij fouten doet het systeem een stapje terug en geeft het extra uitleg. Fouten maken is hier dus letterlijk onderdeel van het leerproces!',
      color: 'sky',
      pinned: true
    });
    return tips;
  }, [selectedChild, childData, getLearnerModuleMastery, onNavigate, decay.perModule, moduleMastery]);
  // W4-2: filter dismissed (within hide-window) + deterministic week-shuffle.
  // Pinned tip ('platform-info') is always kept and always rendered last.
  const visibleTips = useMemo<GeneratedTip[]>(() => {
    const active = generatedTips.filter(tip => tip.pinned || !isTipDismissed(dismissals, tip.id));
    const pinned = active.filter(t => t.pinned);
    const cyclable = active.filter(t => !t.pinned);
    const weekOfYear = getISOWeekOfYear(new Date());
    const seed = `${activeAccount.id}-${selectedChild?.id ?? 'none'}-w${weekOfYear}`;
    const {
      shuffled
    } = shuffleWithSeed(cyclable, seed);
    return [...shuffled, ...pinned];
  }, [generatedTips, dismissals, activeAccount.id, selectedChild?.id]);
  const handleDismiss = useCallback(async (tip: GeneratedTip) => {
    if (tip.pinned) return;
    const ts = Date.now();
    setDismissals(prev => ({
      ...prev,
      [tip.id]: ts
    }));
    await setParentTipDismissed(activeAccount.id, tip.id, ts);
    toast('Tip verborgen', {
      description: 'Deze tip komt over 14 dagen weer terug.',
      action: {
        label: 'Ongedaan maken',
        onClick: () => {
          setDismissals(prev => {
            const next = {
              ...prev
            };
            delete next[tip.id];
            return next;
          });
          void clearParentTipDismissal(activeAccount.id, tip.id);
        }
      },
      duration: 5000
    });
  }, [activeAccount.id]);
  if (!selectedChild) {
    return <PageLayout title="Tips" description="Persoonlijk advies van Djay" data-id="element-8291">
        <div className="max-w-4xl mx-auto pb-12" data-id="element-8292">
          <div className="flex flex-col items-center justify-center py-20 px-4 text-center" data-id="element-8293">
            <DjayNarrator size="lg" emotion="thinking" animate data-id="element-8294" />
            <h2 className="text-xl font-bold text-slate-900 mt-6 mb-2" data-id="element-8295">
              Koppel eerst een kind
            </h2>
            <p className="text-slate-500 max-w-md" data-id="element-8296">
              Om persoonlijke tips te kunnen geven, moet je eerst een kind
              koppelen. Ga naar Instellingen om een kind te koppelen met een
              koppelcode.
            </p>
          </div>
        </div>
      </PageLayout>;
  }
  const colorClasses: Record<TipColor, string> = {
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-900',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    rose: 'bg-rose-50 border-rose-200 text-rose-900',
    sky: 'bg-sky-50 border-sky-200 text-sky-900'
  };
  const iconBgClasses: Record<TipColor, string> = {
    amber: 'bg-amber-100',
    indigo: 'bg-indigo-100',
    emerald: 'bg-emerald-100',
    rose: 'bg-rose-100',
    sky: 'bg-sky-100'
  };
  const actionTextClasses: Record<TipColor, string> = {
    amber: 'text-amber-700 hover:text-amber-800',
    indigo: 'text-indigo-700 hover:text-indigo-800',
    emerald: 'text-emerald-700 hover:text-emerald-800',
    rose: 'text-rose-700 hover:text-rose-800',
    sky: 'text-sky-700 hover:text-sky-800'
  };
  const dismissBtnClasses: Record<TipColor, string> = {
    amber: 'text-amber-500 hover:bg-amber-100 hover:text-amber-700',
    indigo: 'text-indigo-500 hover:bg-indigo-100 hover:text-indigo-700',
    emerald: 'text-emerald-500 hover:bg-emerald-100 hover:text-emerald-700',
    rose: 'text-rose-500 hover:bg-rose-100 hover:text-rose-700',
    sky: 'text-sky-500 hover:bg-sky-100 hover:text-sky-700'
  };
  return <PageLayout title="Tips" description="Persoonlijk advies van Djay" data-id="element-8297">
      <div className="max-w-5xl mx-auto space-y-8 pb-12" data-id="element-8298">
        <ChildSelectorBar linkedLearners={linkedLearners} selectedChildId={selectedChild.id} onSelect={setViewingLearner} data-id="element-8299" />

        <div className="flex items-center gap-4 mb-6" data-id="element-8300">
          <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center border border-emerald-100 overflow-hidden flex-shrink-0" data-id="element-8301">
            <DjayNarrator size="md" emotion="thinking" animate data-id="element-8302" />
          </div>
          <div className="flex-1" data-id="element-8303">
            <h2 className="text-lg font-black text-slate-900 mb-1" data-id="element-8304">
              Persoonlijke tips voor {selectedChild.displayName}
            </h2>
            <p className="text-sm text-slate-600" data-id="element-8305">
              Ik heb het leergedrag geanalyseerd. Hier zijn mijn aanbevelingen!
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-8306">
          <AnimatePresence initial={false} data-id="element-8307">
            {visibleTips.map((tip, i) => <motion.div key={tip.id} layout initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10,
            scale: 0.98
          }} transition={{
            delay: i * 0.05,
            duration: 0.25
          }} data-id="element-8308">
                <Card className={`p-6 border h-full flex flex-col relative ${colorClasses[tip.color]} shadow-sm`} data-id="element-8309">
                  {!tip.pinned && <button onClick={() => handleDismiss(tip)} aria-label={`Verberg tip: ${tip.title}`} title="Verberg deze tip (14 dagen)" className={`absolute top-3 right-3 w-7 h-7 rounded-lg flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-current ${dismissBtnClasses[tip.color]}`} data-id="element-8310">
                      <X className="w-4 h-4" data-id="element-8311" />
                    </button>}
                  <div className="flex items-center gap-4 mb-4 pr-8" data-id="element-8312">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBgClasses[tip.color]}`} data-id="element-8313">
                      {tip.icon}
                    </div>
                    <h3 className="text-lg font-black leading-tight" data-id="element-8314">
                      {tip.title}
                    </h3>
                  </div>
                  <p className="text-sm font-medium opacity-80 leading-relaxed flex-1" data-id="element-8315">
                    {tip.content}
                  </p>
                  {tip.action && <div className="mt-4 pt-4 border-t border-black/5" data-id="element-8316">
                      <button onClick={tip.action.onClick} className={`text-sm font-bold flex items-center gap-1 transition-colors ${actionTextClasses[tip.color]}`} data-id="element-8317">
                        {tip.action.label}
                        <ArrowRight className="w-4 h-4" data-id="element-8318" />
                      </button>
                    </div>}
                </Card>
              </motion.div>)}
          </AnimatePresence>
        </div>

        {visibleTips.length === 1 &&
      // Only the pinned platform-info card remains — everything else dismissed.
      <Card className="p-6 border border-dashed border-slate-200 bg-slate-50/60 text-center" data-id="element-8319">
            <p className="text-sm text-slate-600" data-id="element-8320">
              Je hebt alle tips verborgen. Ze komen vanzelf weer terug zodra ze
              relevant zijn, of na 14 dagen.
            </p>
          </Card>}

        <Card className="p-8 border-slate-200 shadow-sm bg-white mt-8 text-center" data-id="element-8321">
          <Lightbulb className="w-10 h-10 text-amber-400 mx-auto mb-4" data-id="element-8322" />
          <h3 className="text-xl font-black text-slate-900 mb-2" data-id="element-8323">
            Heb je vragen?
          </h3>
          <p className="text-slate-600 mb-6 max-w-lg mx-auto" data-id="element-8324">
            De docent van {selectedChild.displayName} heeft ook toegang tot deze
            gegevens en kan je meer vertellen over de voortgang in de klas.
          </p>
        </Card>
      </div>
    </PageLayout>;
}